import React from 'react';
import { cva } from 'class-variance-authority';
import { motion } from 'motion/react';
import { duration } from './primitives/tokens';
import styles from './Badge.module.css';

// ── CVA ─────────────────────────────────────────────────────────────────────

const badge = cva(styles.root);

// ── Types ─────────────────────────────────────────────────────────────────────

type BadgeStatus = 'default' | 'success' | 'processing' | 'error' | 'warning';

interface BadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Number to display */
  count?: number;
  /** Show as a dot without number */
  dot?: boolean;
  /** Maximum count before showing N+ */
  overflowCount?: number;
  /** Show badge when count is zero */
  showZero?: boolean;
  /** Status dot mode */
  status?: BadgeStatus;
  /** Custom color */
  color?: string;
  /** Pixel offset [right, top] */
  offset?: [number, number];
  /** Status text (rendered beside the dot in status mode) */
  text?: string;
  children?: React.ReactNode;
}

const statusColors: Record<BadgeStatus, string> = {
  default: 'var(--outline-variant)',
  success: 'var(--success)',
  processing: 'var(--primary)',
  error: 'var(--error)',
  warning: 'var(--warning)',
};

/**
 * Badge — notification count or status dot indicator.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | count | number | — | Number to display |
 * | dot | boolean | false | Show as dot instead of number |
 * | overflowCount | number | 99 | Max before showing "N+" |
 * | showZero | boolean | false | Show when count is 0 |
 * | status | string | — | Status dot mode (no count, standalone) |
 * | color | string | — | Custom badge color |
 * | offset | [right, top] | — | Position offset in pixels |
 * | text | string | — | Label beside status dot |
 *
 * @example
 * <Badge count={5}><Avatar /></Badge>
 * <Badge dot><IconButton>...</IconButton></Badge>
 * <Badge status="success" text="Online" />
 */
export function Badge({
  count,
  dot,
  overflowCount = 99,
  showZero = false,
  status,
  color,
  offset,
  text,
  children,
  style,
  className,
  ...props
}: BadgeProps) {
  // Status-only mode (no children, just dot + text)
  if (status && !children) {
    const dotColor = color ?? statusColors[status];
    return (
      <div
        className={`${styles.statusRoot}${className ? ` ${className}` : ''}`}
        style={style}
        {...props}
      >
        <span className={styles.dotWrapper}>
          <span
            className={styles.statusDot}
            // Dot color is runtime — CSS custom property
            style={{ '--dot-color': dotColor } as React.CSSProperties}
          />
          {status === 'processing' && (
            <span
              className={styles.pulse}
              aria-hidden="true"
              // Pulse color matches dot — CSS custom property
              style={{ '--dot-color': dotColor } as React.CSSProperties}
            />
          )}
        </span>
        {text && (
          <span className={styles.statusText}>
            {text}
          </span>
        )}
      </div>
    );
  }

  const showCount = count !== undefined && (count > 0 || showZero);
  const displayCount = count !== undefined && count > overflowCount ? `${overflowCount}+` : count;
  const badgeColor = color ?? 'var(--error)';

  const shouldShow = dot || showCount;

  // Offset values are runtime — CSS custom properties
  const topVal = offset ? `${offset[1]}px` : '0px';
  const rightVal = offset ? `${-offset[0]}px` : '0px';

  return (
    <div
      className={badge({ className })}
      style={style}
      {...props}
    >
      {children}
      {shouldShow && (
        <span
          aria-label={!dot && count !== undefined ? `${displayCount} notifications` : undefined}
          className={dot ? styles.badgeDot : styles.badgeCount}
          // Position offset and color are runtime — CSS custom properties
          style={{
            '--badge-top': topVal,
            '--badge-right': rightVal,
            '--badge-color': badgeColor,
          } as React.CSSProperties}
        >
          {!dot && (
            <motion.span
              key={displayCount}
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'tween', duration: duration.slow / 1000 }}
            >
              {displayCount}
            </motion.span>
          )}
        </span>
      )}
    </div>
  );
}
